import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPhone, faEnvelope, faMapMarkerAlt, faPaperPlane } from '@fortawesome/free-solid-svg-icons';

function ContactForm() {
    const initialFormState = {
        nombre: '',
        apellido: '',
        email: '',
        celular: '',
        provincia: '',
        localidad: '',
        ubicacion: '',
        mensaje: ''
    };

    const [formData, setFormData] = useState(initialFormState);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [id]: value
        }));
        
        // Limpiar el error específico al cambiar el valor
        if (errors[id]) {
            setErrors(prevErrors => {
                const newErrors = { ...prevErrors };
                delete newErrors[id];
                return newErrors;
            });
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.nombre.trim()) newErrors.nombre = "El nombre es obligatorio";
        if (!formData.apellido.trim()) newErrors.apellido = "El apellido es obligatorio";
        
        if (!formData.email.trim() && !formData.celular.trim()) {
            newErrors.medioContacto = "Debes proporcionar un correo electrónico o un número de celular";
        } else {
            if (formData.email.trim() && !/^\S+@\S+\.\S+$/.test(formData.email)) {
                newErrors.email = "Formato de correo electrónico inválido";
            }
            if (formData.celular.trim() && !/^\d{10}$/.test(formData.celular)) {
                newErrors.celular = "El número debe contener 10 dígitos";
            }
        }
        
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        setErrors(validationErrors);
        
        if (Object.keys(validationErrors).length === 0) {
            setIsSubmitting(true);
            
            try {
                // Simulación de envío (reemplazar con tu lógica de API real)
                await new Promise(resolve => setTimeout(resolve, 1000));
                console.log("Formulario enviado", formData);
                
                // Resetear formulario después del envío exitoso
                setFormData(initialFormState);
                setSubmitSuccess(true);
                
                // Ocultar mensaje de éxito después de 5 segundos
                setTimeout(() => setSubmitSuccess(false), 5000);
            } catch (error) {
                console.error("Error al enviar formulario:", error);
                setErrors({ submit: "Hubo un error al enviar el formulario. Intente nuevamente." });
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    return (
        <section id="contacto" className="bg-white py-16 md:py-24 overflow-x-hidden">
            <div className="container mx-auto px-4 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
                {/* Información de Contacto */}
                <div className="flex flex-col justify-center space-y-6" data-aos="fade-right">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-800">Ponete en contacto con nosotros</h2>
                    <p className="text-lg text-gray-600">
                        Para cualquier consulta o recomendación, llame al:
                        <span className="inline-flex items-center ml-2">
                            <FontAwesomeIcon icon={faPhone} className="text-blue-500 mr-1" /> 
                            <a href="tel:02644383030" className="hover:text-blue-500 transition-colors">0264 4383030</a>
                        </span> 
                        o complete el siguiente formulario.
                    </p>
                    
                    <div className="bg-gray-50 p-6 rounded-lg shadow-md mt-4">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">Información de Contacto</h3>
                        <ul className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faMapMarkerAlt} className="text-blue-500 mt-1 mr-3" />
                                <span>
                                    Calle 13 oeste N° 3155<br />
                                    Pocito, San Juan (5427)
                                </span>
                            </li>
                            <li className="flex items-center">
                                <FontAwesomeIcon icon={faEnvelope} className="text-blue-500 mr-3" />
                                <a href="mailto:vaquer@vaquerperforaciones.com" className="text-blue-500 hover:underline">
                                    vaquer@vaquerperforaciones.com
                                </a>
                            </li>
                            <li className="flex items-center">
                                <FontAwesomeIcon icon={faPhone} className="text-blue-500 mr-3" />
                                <span>Tel: <a href="tel:02644383030" className="hover:text-blue-500">0264 4383030</a></span>
                            </li>
                            <li className="flex items-center">
                                <FontAwesomeIcon icon={faPhone} className="text-blue-500 mr-3" />
                                <span>Cel: <a href="tel:02644802789" className="hover:text-blue-500">0264 4802789</a></span>
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Formulario de Contacto */}
                <div data-aos="fade-left">
                    <form onSubmit={handleSubmit} className="bg-gray-50 p-6 md:p-10 rounded-lg shadow-2xl">
                        {submitSuccess && (
                            <div className="mb-6 p-4 bg-green-100 border border-green-200 text-green-700 rounded-md flex items-center">
                                <FontAwesomeIcon icon={faPaperPlane} className="mr-2" />
                                Mensaje enviado correctamente. Nos pondremos en contacto pronto.
                            </div>
                        )}
                        
                        {errors.submit && (
                            <div className="mb-6 p-4 bg-red-100 border border-red-200 text-red-700 rounded-md">
                                {errors.submit}
                            </div>
                        )}

                        <div className="space-y-6">
                            {/* Nombre y Apellido */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                <div>
                                    <label htmlFor="nombre" className="block text-gray-700 font-semibold mb-1">
                                        Nombre <span className="text-red-500">*</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        id="nombre" 
                                        value={formData.nombre} 
                                        onChange={handleChange} 
                                        className={`p-3 w-full rounded-md border ${errors.nombre ? 'border-red-500 bg-red-50' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition`} 
                                    />
                                    {errors.nombre && <p className="text-red-500 text-sm mt-1">{errors.nombre}</p>}
                                </div>
                                <div>
                                    <label htmlFor="apellido" className="block text-gray-700 font-semibold mb-1">
                                        Apellido <span className="text-red-500">*</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        id="apellido" 
                                        value={formData.apellido} 
                                        onChange={handleChange} 
                                        className={`p-3 w-full rounded-md border ${errors.apellido ? 'border-red-500 bg-red-50' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition`} 
                                    />
                                    {errors.apellido && <p className="text-red-500 text-sm mt-1">{errors.apellido}</p>}
                                </div>
                            </div>

                            {/* Email y Teléfono */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                <div>
                                    <label htmlFor="email" className="block text-gray-700 font-semibold mb-1">
                                        Correo Electrónico <span className="text-sm text-gray-500">*</span>
                                    </label>
                                    <input 
                                        type="email" 
                                        id="email" 
                                        value={formData.email} 
                                        onChange={handleChange} 
                                        className={`p-3 w-full rounded-md border ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition`} 
                                    />
                                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                                </div>
                                <div>
                                    <label htmlFor="celular" className="block text-gray-700 font-semibold mb-1">
                                        Celular <span className="text-sm text-gray-500">*</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        id="celular" 
                                        value={formData.celular} 
                                        onChange={handleChange} 
                                        placeholder="Ej: 2644123456" 
                                        className={`p-3 w-full rounded-md border ${errors.celular ? 'border-red-500 bg-red-50' : 'border-gray-300'} focus:ring-2 focus:ring-blue-500 focus:border-transparent transition`} 
                                    />
                                    {errors.celular && <p className="text-red-500 text-sm mt-1">{errors.celular}</p>}
                                </div>
                            </div>
                            {errors.medioContacto && (
                                <p className="text-red-500 text-sm -mt-2">{errors.medioContacto}</p>
                            )}
                            <p className="text-gray-500 text-sm -mt-2">* Proporcione al menos un medio de contacto (email o celular)</p>

                            {/* Ubicación */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                                <div>
                                    <label htmlFor="provincia" className="block text-gray-700 font-semibold mb-1">
                                        Provincia
                                    </label>
                                    <input 
                                        type="text" 
                                        id="provincia" 
                                        value={formData.provincia} 
                                        onChange={handleChange} 
                                        className="p-3 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" 
                                    />
                                </div>
                                <div>
                                    <label htmlFor="localidad" className="block text-gray-700 font-semibold mb-1">
                                        Localidad
                                    </label>
                                    <input 
                                        type="text" 
                                        id="localidad" 
                                        value={formData.localidad} 
                                        onChange={handleChange} 
                                        className="p-3 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" 
                                    />
                                </div>
                            </div>

                            {/* Ubicación y Mensaje */}
                            <div>
                                <label htmlFor="ubicacion" className="block text-gray-700 font-semibold mb-1">
                                    Ubicación
                                </label>
                                <input 
                                    type="text" 
                                    id="ubicacion" 
                                    value={formData.ubicacion} 
                                    onChange={handleChange} 
                                    placeholder="Dirección detallada o referencias" 
                                    className="p-3 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition" 
                                />
                            </div>

                            <div>
                                <label htmlFor="mensaje" className="block text-gray-700 font-semibold mb-1">
                                    Mensaje
                                </label>
                                <textarea 
                                    id="mensaje" 
                                    value={formData.mensaje} 
                                    onChange={handleChange} 
                                    rows="4"
                                    placeholder="Escribe tu consulta o mensaje aquí..." 
                                    className="p-3 w-full rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition resize-y" 
                                ></textarea>
                            </div>

                            {/* Botón Enviar */}
                            <div>
                                <button 
                                    type="submit" 
                                    disabled={isSubmitting}
                                    className="w-full bg-blue-500 text-white font-semibold p-3 rounded-md hover:bg-blue-600 transition duration-200 flex items-center justify-center"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="mr-2 animate-spin">⏳</span>
                                            Enviando...
                                        </>
                                    ) : (
                                        <>
                                            Enviar mensaje
                                            <FontAwesomeIcon icon={faPaperPlane} className="ml-2" />
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}

export default ContactForm;